import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CalendarComponent from "../../Ui_Components/Calendar";
import { ReactComponent as CalendarIcon } from "../../../assects/svg/client_details/calendar-icon.svg";

interface TabNavigationProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  selectedDate,
  setSelectedDate
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Get today's date with time set to midnight for accurate comparison
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Normalize date to midnight for comparison
  const normalizeDate = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  // Check if selected date is today
  const isToday = (date: Date) => {
    const compareDate = normalizeDate(date);
    const today = getToday();
    return compareDate.getTime() === today.getTime();
  };

  // Format date for display
  const formatDate = (date: Date) => {
    if (isToday(date)) return "Today";

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    const compareDate = normalizeDate(date);

    if (compareDate.getTime() === yesterday.getTime()) return "Yesterday";

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Navigate to previous day
  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    newDate.setHours(0, 0, 0, 0);
    setSelectedDate(newDate);
  };

  // Navigate to next day (only if not today)
  const handleNextDay = () => {
    const currentNormalized = normalizeDate(selectedDate);
    const today = getToday();
    
    if (currentNormalized.getTime() >= today.getTime()) {
      return;
    }
    
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(0, 0, 0, 0);
    
    const newNormalized = normalizeDate(newDate);
    if (newNormalized.getTime() <= today.getTime()) {
      setSelectedDate(newDate);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    const normalizedDate = normalizeDate(date);
    setSelectedDate(normalizedDate);
    setShowCalendar(false);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  return (
    <div className="mx-6 mb-3">
      <div className="border-b border-gray-200 px-4 flex items-center justify-between">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab("logs")}
            className={`py-2 relative text-[14px] font-inter whitespace-nowrap ${
              activeTab === "logs"
                ? "text-[#00A9A9] font-semibold"
                : "text-[#1F1F1F] font-medium"
            }`}
          >
            All logs
            {activeTab === "logs" && (
              <div className="absolute bottom-0 -left-4 -right-4 h-0.5 bg-[#00A9A9]" />
            )}
          </button>

          <button
            onClick={() => setActiveTab("insights")}
            className={`py-2 relative text-[14px] font-inter whitespace-nowrap ${
              activeTab === "insights"
                ? "text-[#00A9A9] font-semibold"
                : "text-[#1F1F1F] font-medium"
            }`}
          >
            Insights
            {activeTab === "insights" && (
              <div className="absolute bottom-0 -left-4 -right-4 h-0.5 bg-[#00A9A9]" />
            )}
          </button>

          <button
            onClick={() => setActiveTab("recipe")}
            className={`py-2 relative text-[14px] font-inter whitespace-nowrap ${
              activeTab === "recipe"
                ? "text-[#00A9A9] font-semibold"
                : "text-[#1F1F1F] font-medium"
            }`}
          >
            Recipe
            {activeTab === "recipe" && (
              <div className="absolute bottom-0 -left-3 -right-3 h-0.5 bg-[#00A9A9]" />
            )}
          </button>

          <button
            onClick={() => setActiveTab("nutritionist")}
            className={`py-2 relative text-[14px] font-inter whitespace-nowrap ${
              activeTab === "nutritionist"
                ? "text-[#00A9A9] font-semibold"
                : "text-[#1F1F1F] font-medium"
            }`}
          >
            Nutritionist
            {activeTab === "nutritionist" && (
              <div className="absolute bottom-0 -left-4 -right-4 h-0.5 bg-[#00A9A9]" />
            )}
          </button>

        </div>

        {activeTab === "logs" && (
          <div className="flex items-center gap-1 ml-4 relative">
            <button
              onClick={handlePreviousDay}
              className="p-1 hover:bg-[#0DB5B5] rounded transition"
              aria-label="Previous day"
            >
              <ChevronLeft className="w-4 h-4 3xl:w-5 3xl:h-5" />
            </button>

            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="flex items-center gap-2 px-3 py-1 hover:bg-gray-50 rounded transition"
            >
              <span className="text-[14px] 3xl:text-[16px] text-[#003E3E] font-inter font-medium whitespace-nowrap">
                {formatDate(selectedDate)}
              </span>
              <CalendarIcon className="w-4 h-4 text-gray-600" />
            </button>

            <button
              onClick={handleNextDay}
              disabled={isToday(selectedDate)}
              className={`p-1 rounded transition ${
                isToday(selectedDate)
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-[#0DB5B5] cursor-pointer"
              }`}
              aria-label="Next day"
            >
              <ChevronRight className="w-4 h-4 3xl:w-5 3xl:h-5" />
            </button>

            {showCalendar && (
              <div ref={calendarRef} className="absolute top-full right-0 mt-2 z-50">
                <CalendarComponent
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  onClose={() => setShowCalendar(false)}
                  maxDate={getToday()}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TabNavigation;